import React, { createContext, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";

const AccesosContext = createContext();

export const useAccesos = () => useContext(AccesosContext);

const safeParse = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
};

// ✅ Organizador robusto (soporta distintos nombres de "padre" y tipos)
const organizeMenuItems = (raw = []) => {
  const items = (raw?.$values ?? raw ?? []).filter(Boolean);

  const getParentId = (item) =>
    item?.id_menu_padre ??
    item?.id_menuPadre ??
    item?.idMenuPadre ??
    item?.id_padre ??
    item?.idPadre ??
    null;

  const lookup = new Map();
  items.forEach((item) => {
    lookup.set(item.id, { ...item, children: [] });
  });

  const roots = [];
  items.forEach((item) => {
    const node = lookup.get(item.id);
    const parentId = getParentId(item);

    const isRoot =
      parentId === null ||
      parentId === undefined ||
      parentId === 0 ||
      parentId === "0" ||
      parentId === "";

    if (isRoot) {
      roots.push(node);
    } else {
      const parent = lookup.get(Number.isNaN(+parentId) ? parentId : +parentId) || lookup.get(parentId);
      if (parent) parent.children.push(node);
      else roots.push(node); // si el padre no está, no lo perdemos
    }
  });

  const sortMenus = (menu) => {
    menu.sort((a, b) => (a.posicion ?? 0) - (b.posicion ?? 0));
    menu.forEach((m) => m.children?.length && sortMenus(m.children));
  };
  sortMenus(roots);

  return roots;
};

export const AccesosProvider = ({ children, initialData = [], initialPermisos = [] }) => {
  // ✅ accesos FLAT (igual que backend)
  const [accesos, setAccesosState] = useState(() => {
    const parsed = safeParse("accesos", initialData);
    return (parsed?.$values ?? parsed ?? []).filter(Boolean);
  });

  const [permisos, setPermisosState] = useState(() => {
    const parsed = safeParse("permisos", initialPermisos);
    return (parsed?.$values ?? parsed ?? []).filter(Boolean);
  });

  // ✅ árbol listo para SideNav si lo usa
  const menuTree = useMemo(() => organizeMenuItems(accesos), [accesos]);

  // ✅ setters que también persisten y limpian null
  const setAccesos = (arr) => {
    const clean = (arr?.$values ?? arr ?? []).filter(Boolean);
    setAccesosState(clean);
    localStorage.setItem("accesos", JSON.stringify(clean));
  };

  const setPermisos = (arr) => {
    const clean = (arr?.$values ?? arr ?? []).filter(Boolean);
    setPermisosState(clean);
    localStorage.setItem("permisos", JSON.stringify(clean));
  };

  // Función para verificar los permisos de un usuario
  const tienePermiso = (nombrePermiso) => {
    if (!nombrePermiso) return false;
    if (!Array.isArray(permisos) || permisos.length === 0) return false;
    
    return permisos.some(p => 
      p.permiso?.trim().toLowerCase() === nombrePermiso.trim().toLowerCase()
    );
  };

  // Obtener los permisos que pertenecen a un id_menu específico
  const permisosPorMenu = (idMenu) => {
    if (!idMenu || !Array.isArray(permisos) || permisos.length === 0) return [];
    return permisos.filter(p => p.id_menu === idMenu || p.idMenu === idMenu);
  };

  return (
    <AccesosContext.Provider value={{ accesos, setAccesos, permisos, setPermisos, menuTree, tienePermiso, permisosPorMenu }}>
      {children}
    </AccesosContext.Provider>
  );
};

AccesosProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialData: PropTypes.array,
  initialPermisos: PropTypes.array,
};
